import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

// styles
import './style.scss';

// antd component
import { Form, Radio, Button } from 'antd';

// containers
import SelectFieldAxios from 'containers/SelectFieldAxios';
// components
import CustomSelect from 'components/Forms/Fields/CustomSelect';
import CustomDate from 'components/Forms/Fields/CustomDate';
import CustomTime from 'components/Forms/Fields/CustomTime';

// utils
import { RECURRING, DAY_OF_WEEK } from 'utils/helpers/helper.constant';
import { clean, getArrayIds, getObjId } from 'utils/helpers/helper.general';
// helper for forms
import {
  setTimeRangeAfterChanges,
  setDateRangeAfterChanges,
} from 'utils/forms/forms.utils';
// hooks
import { useRefState } from 'utils/hooks';


function DurationForm({
  formData,
  type,
  selectedStudent,
  // actions
  nextStep,
  prevStep,
  result,
  // default
  className,
}) {
  const childClassName = classNames('form-absence', className);

  const timeFormat = 'HH:mm';
  const dateFormat = 'DD.MM.YYYY';

  const now = moment(formData?.date) || moment();
  const initFormData = {
    duration: 'day',
    date: moment(now, dateFormat),
    courses: [],
    // for duration more than 1 day
    date_from: null,
    time_from: null,
    date_to: null,
    time_to: null,
    // For  "Absence récurrente" part
    recurring: null,
    recurring_date: null,
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [durationType, setDurationType] = useState('day');
  const [recurringType, setRecurringType] = useState('every_week');
  const [isRepeate, setIsRepeate] = useState(false);

  const [endponitObj, setEndponitObj] = useState(false);
  const [currentDay, setCurrentDay] = useState(now);

  const [hasCourse, setHasCourse] = useState(false);
  const [currentCourse, currentCourseRef, setCurrentCourse] = useRefState([]);

  const [hasRecur, setHasRecur] = useState(false);

  const [AbsFormDuration] = Form.useForm();

  const recurringOption = RECURRING;
  const options = [
    {
      key: 'day',
      value: 'day',
      label: (
        <>
          <span className="label__title">Une journée</span>
        </>
      ),
    },
    {
      key: 'more',
      value: 'more',
      label: (
        <>
          <span className="label__title">Plus d’une journée</span>
        </>
      ),
    },
  ];

  // Clearing the state after unmounting a component
  useEffect(() => {
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state for SelectFieldAxios component
      setHasCourse(false);
      setEndponitObj(false);
    };
  }, []);

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    if (formData) {
      const hasValFormData = clean(formData);
      setIsDisabled(false);
      AbsFormDuration.setFieldsValue({
        duration: hasValFormData?.duration || initFormData?.duration,
        // for duration more than 1 day
        date_from: moment(hasValFormData?.date_from) || null,
        time_from: moment(hasValFormData?.time_from) || null,
        date_to: moment(hasValFormData?.date_to) || null,
        time_to: moment(hasValFormData?.time_to) || null,
        // For  "Absence récurrente" part
        recurring: hasValFormData?.recurring || null,
        recurring_date: moment(hasValFormData?.recurring_date) || null,
      });

      if (
        hasValFormData.hasOwnProperty('courses') &&
        hasValFormData.courses.length > 0
      ) {
        AbsFormDuration.setFieldsValue({
          courses: getArrayIds(hasValFormData.courses),
        });
      }

      if (hasValFormData.hasOwnProperty('recurring')) {
        setRecurringType(hasValFormData.recurring);
        setIsRepeate(true);
        setHasRecur(true);
      }
      if (hasValFormData.hasOwnProperty('duration')) {
        setDurationType(hasValFormData?.duration);
      }

      if (hasValFormData.hasOwnProperty('date')) {
        setCurrentDay(moment(hasValFormData.date));
        setEndponitObj({
          course_week:
            DAY_OF_WEEK[moment(hasValFormData.date).isoWeekday() - 1]?.value,
        });
      } else {
        setCurrentDay(now);
        setEndponitObj({
          course_week: DAY_OF_WEEK[now.isoWeekday() - 1]?.value,
        });
      }
    } else {
      setCurrentDay(now);
      setEndponitObj({
        course_week: DAY_OF_WEEK[now.isoWeekday() - 1]?.value,
      });
    }
  }, [formData]);

  /*
   * Check current date and day of the week
   * for generate Courses list
   */
  useEffect(() => {
    if (currentDay) {
      // 1 - Monday, 7 - Sunday
      const week = currentDay.isoWeekday();
      setEndponitObj({
        course_week: DAY_OF_WEEK[week - 1]?.value,
      });
    }
  }, [currentDay]);

  useEffect(() => {
    if (currentDay && isRepeate && !hasRecur) {
      const newDate = getRecurringDate(recurringType, currentDay);
      AbsFormDuration.setFieldsValue({
        recurring_date: newDate,
      });
    }
  }, [isRepeate, hasRecur, currentDay]);

  const resetInitFileds = durationVal => {
    let initFileds = {};
    switch (durationVal) {
      case 'day': {
        initFileds = {
          date: moment(now, dateFormat),
          // for duration more than 1 day
          date_from: null,
          time_from: null,
          date_to: null,
          time_to: null,
        };
        break;
      }
      case 'more': {
        initFileds = {
          date: null,
          // for duration more than 1 day
          date_from: moment(now, dateFormat),
          time_from: moment(now, timeFormat),
          date_to: moment(now, dateFormat),
          time_to: moment(now, timeFormat),
        };
        break;
      }
    }
    AbsFormDuration.setFieldsValue({
      courses: [],
      // For  "Absence récurrente" part
      recurring: null,
      recurring_date: null,
      ...initFileds,
    });
    setHasRecur(false);
  };

  const onFinish = values => {
    let emptyKey = [];
    for (let initKey in initFormData) {
      if (!values.hasOwnProperty(initKey)) {
        emptyKey.push({
          [initKey]: null,
        });
      }
    }
    const mapped = Object.assign({}, ...emptyKey);

    if (values.hasOwnProperty('courses')) {
      const coursesObj =
      values?.courses.length > 0 &&
        values?.courses.map(course => {
          let obj = null;
          currentCourseRef?.current && currentCourseRef?.current.map(cc => {
            if (course == cc?._id || course?._id == cc?._id) {
              obj = cc;
            }
          })
          return obj;
        }).filter(el => el !== null);
      values.courses = coursesObj || [];
    }

    const hole_day_pass = durationType == 'day' && values?.courses?.length == 0;

    result({
      ...values,
      ...mapped,
      hole_day_pass: hole_day_pass
    });
    
    nextStep !== undefined && nextStep();
    AbsFormDuration.resetFields();
  };

  const getRecurringDate = (dateType, currentDate) => {
    if (dateType == 'every_day') {
      return moment(currentDate).add(1, 'days');
    } else if (dateType == 'every_week') {
      return moment(currentDate).add(7, 'days');
      //.endOf('isoWeek');
    } else if (dateType == 'every_month') {
      return moment(currentDate).add(1, 'months');
    }
  };

  // Select courses by current child and current day of the week
  const selectCourses = useCallback(objSend => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: 'Cours',
      name: 'courses',
      endponit: `students/courses/${getObjId(selectedStudent)}`,
      required: false,
      displayLabel: true,
      type: 'axios-select',
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      itemCallback: item => {
        return {
          key: item?._id,
          title: `<span class="time">${moment(item?.course_time_start).format(
            'HH:mm',
          )}</span> <span class="cours-name">${item?.subject?.name}</span>`,
          label: `${item?.subject?.name}`,
          value: item?._id,
          color: item?.subject?.color,
          item: item,
        };
      },
    };

    return (
      objSend && !!selectedStudent && (
        <>
          <SelectFieldAxios
            mode="multiple"
            sortBy="title"
            className="axios-select"
            tagRender={newElementSelect.tagRender}
            endponit={newElementSelect.endponit}
            endponitSend={objSend}
            placeholder="Selectionner le(s) cours"
            getResponse={response => {
              if (response?.data.length > 0) {
                setHasCourse(true);
                setCurrentCourse(response?.data);
              } else {
                setHasCourse(false);
              }
            }}
            itemCallback={newElementSelect.itemCallback}
            item={newElementSelect}
            isSearch={false}
          />
          {!hasCourse && (
            <span className="alert axios-alert">
              Il n'y a pas de cours à cette date.
            </span>
          )}
        </>
      )
    );
  }, [hasCourse, selectedStudent]);

  const absFromDurationValuesChange = useCallback((values, allValues) => {
    setIsDisabled(false);
    if (values?.duration) {
      setDurationType(values?.duration);
      setIsRepeate(false);
      resetInitFileds(values?.duration);
    }

    setTimeRangeAfterChanges(AbsFormDuration, allValues);
    setDateRangeAfterChanges(AbsFormDuration, allValues);
  }, [AbsFormDuration]);

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormDuration}
        className="form-absence__form"
        name={`form_absence_${type}_duration`}
        initialValues={initFormData}
        onFinish={onFinish}
        onValuesChange={absFromDurationValuesChange}
      >
        <Form.Item name="duration" label="Durée">
          <Radio.Group
            className="radio-group radio-group--horizontal"
            options={options}
            optionType="Radio"
          />
        </Form.Item>

        {durationType == 'more' ? (
          <>
            <div className="form-row">
              <h5 className="form-row__label">Du</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_from"
                 // disabledDate={disabledDateFrom}
                />
                <CustomTime name="time_from" />
              </div>
            </div>
            <div className="form-row">
              <h5 className="form-row__label">Au</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_to"
                 // disabledDate={disabledDateFrom}
                />
                <CustomTime name="time_to" />
              </div>
            </div>
          </>
        ) : (
          <>
            <CustomDate
              name="date"
              //disabledDate={disabledDateFrom}
              value={currentDay}
              onChange={val => {
                setCurrentDay(val);
                setHasRecur(false);
                AbsFormDuration.setFieldsValue({
                  courses: [],
                });
              }}
            />
            {endponitObj && selectCourses(endponitObj)}
          </>
        )}

        {!isRepeate && (
          <div
            className="is-repeate-action"
            onClick={() => {
              setIsRepeate(true);
              const newDate = getRecurringDate(recurringType, currentDay);
              AbsFormDuration.setFieldsValue({
                recurring: recurringType,
                recurring_date: newDate,
              });
              setHasRecur(false);
            }}
          >
            Absence récurrente ?
          </div>
        )}

        {isRepeate && (
          <div className="is-repeate-section">
            <div className="is-repeate-header">
              <h4 className="title">Absence récurrente</h4>
              <div
                className="is-repeate-hide"
                onClick={() => {
                  setIsRepeate(false);
                  AbsFormDuration.setFieldsValue({
                    recurring: null,
                    recurring_date: null,
                  });
                  setHasRecur(false);
                }}
              >
                Masquer récurrent
              </div>
            </div>
            <CustomSelect
              label="Récurance"
              placeholder="Selectionne"
              name={`recurring`}
              options={recurringOption}
              onSelect={(LabeledValue, option) => {
                setRecurringType(LabeledValue);
                const newDateSet = getRecurringDate(LabeledValue, currentDay);
                AbsFormDuration.setFieldsValue({
                  recurring: LabeledValue,
                  recurring_date: newDateSet,
                });
                setHasRecur(false);
              }}
            />
            <CustomDate
              label="Date de fin"
              name="recurring_date"
              //disabledDate={disabledDateFrom}
            />
          </div>
        )}

        <div className="absence-actions-wrapper">
          <Button
            className="btn-previous"
            onClick={() => prevStep !== undefined && prevStep()}
          >
            Précédent
          </Button>
          <Button
            className="next-btn"
            form={`form_absence_${type}_duration`}
            htmlType="submit"
            disabled={isDisabled}
          >
            Suivant
          </Button>
        </div>
      </Form>
    </div>
  );
}

DurationForm.defaultProps = {
  type: 'medical',
};

DurationForm.propTypes = {
  type: PropTypes.oneOf(['medical', 'no-medical', 'sickness']).isRequired,
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
};

export default memo(DurationForm);
